import { useState, useEffect, useCallback, useRef } from "react";

/**
 * Runs an async loader and exposes { data, loading, error, retry }.
 * `enabled` lets a section wait for something it depends on (token, user location).
 */
const useAsyncData = (loader, deps, enabled = true) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;
  const runId = useRef(0);

  const run = useCallback(async () => {
    const id = ++runId.current;
    setLoading(true);
    setError(null);
    try {
      const result = await loaderRef.current();
      if (id === runId.current) setData(result);
    } catch (err) {
      if (id === runId.current) {
        setError(err?.response?.data?.text || err?.message || "Something went wrong.");
      }
    } finally {
      if (id === runId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (enabled) run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, run, ...deps]);

  return { data, loading, error, retry: run };
};

export default useAsyncData;
